import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, streamUrl } from "@/lib/api";
import { Hash, Play, Eye, Loader2 } from "lucide-react";

const fmt = (n) => {
  if (n == null) return "0";
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(1)}K`.replace(".0K", "K");
  return `${(n / 1000000).toFixed(1)}M`.replace(".0M", "M");
};

export default function HashtagPage() {
  const { tag } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/hashtags/${encodeURIComponent(tag)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tag]);

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-5xl mx-auto px-6 pt-10">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#39FF14] mb-2">
          // hashtag
        </p>
        <h1
          className="font-display font-black text-4xl md:text-5xl mb-3 flex items-center gap-3 break-all glitch"
          data-text={`#${tag}`}
        >
          <Hash className="h-8 w-8 text-[#39FF14] flex-shrink-0" strokeWidth={2.4} />
          <span className="text-[#00FFFF]">#{tag}</span>
        </h1>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-[#39FF14] animate-spin" />
          </div>
        ) : !data || data.video_count === 0 ? (
          <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-white/30">
            // no videos tagged with #{tag} yet
          </div>
        ) : (
          <>
            <div
              data-testid="hashtag-stats"
              className="flex items-center gap-6 mb-10"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  videos
                </p>
                <p className="font-display font-black text-2xl text-[#39FF14]">
                  {fmt(data.video_count)}
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  total views
                </p>
                <p className="font-display font-black text-2xl text-[#00FFFF]">
                  {fmt(data.total_views)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {data.videos.map((v) => (
                <Link
                  key={v.id}
                  to={`/v/${v.id}?src=discover`}
                  data-testid={`hashtag-video-${v.id}`}
                  className="relative aspect-[9/16] bg-black overflow-hidden group border border-white/5 hover:border-[#39FF14]/50 transition-all"
                >
                  <video
                    src={streamUrl(v.id)}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-white/80">
                    <Eye className="h-3 w-3" strokeWidth={2} />
                    {fmt(v.displayed_view_count ?? v.view_count)}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="h-8 w-8 text-white" fill="white" />
                  </div>
                  <p className="absolute bottom-2 left-2 right-2 text-xs text-white truncate">
                    {v.caption}
                  </p>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
